import cx from "classnames";
import { useCallback, useRef } from "react";
import { Link } from "react-router";

import DeltaCounts from "components/DeltaCounts";
import FlashContext from "components/FlashMessages/FlashContext";
import ListEntitiesItem from "components/ListEntitiesItem";
import { prepareName } from "components/state/State";
import VendorVersion from "components/VendorVersion";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import Toggle from "ds/components/Toggle";
import useTypedContext from "hooks/useTypedContext";
import { ansibleRunHasTasks, runHasChanges } from "components/Run/helpers";
import useReviewRun from "shared/Run/useReviewRun";
import { isRunReviewable } from "shared/Run/useReviewRun/accessValidation";
import { Run, RunReviewDecision, Stack, StackConfigVendor } from "types/generated";
import { getRuntimeConfigVendorInfo, getVendorName } from "utils/vendor";
import RunStateBadge from "components/Run/elements/StateBadge";
import { StackContext } from "views/Stack/Context";
import Tooltip from "ds/components/Tooltip";

import { COLUMN_GAP, COLUMN_ORDER_APPROVAL, COLUMN_ORDER_USER } from "../constants";
import DriftDetectedCell from "../../components/DriftDetectedCell";
import RunMetaInfoListItem from "../MetaInfoListItem";
import styles from "./styles.module.css";

type RunElementProps = {
  stackId: Stack["id"];
  run: Run;
  checked: boolean;
  onCheck: (item: Run, checked: boolean) => void;
  vendorConfig?: StackConfigVendor;
  hash: string;
  isAnsible?: boolean;
  selectable?: boolean;
};

const RunElement = (props: RunElementProps) => {
  const { stackId, vendorConfig, run, checked, onCheck, isAnsible, selectable } = props;

  const { reportSuccess } = useTypedContext(FlashContext);
  const { hasAtLeastWriteAccess } = useTypedContext(StackContext);

  const rowRef = useRef<HTMLDivElement>(null);

  const link = `/stack/${stackId}/run/${run.id}`;

  const getDeltaLink = (): { to: string; ariaLabel: string } => {
    if (!run.delta) {
      return { to: link, ariaLabel: "Go to this run" };
    }

    if (isAnsible) {
      return ansibleRunHasTasks(run.delta)
        ? { to: `${link}/tasks`, ariaLabel: "Go to run tasks" }
        : { to: link, ariaLabel: "Go to this run" };
    }

    if (!runHasChanges(run.delta)) {
      return { to: link, ariaLabel: "Go to this run" };
    }

    if (run.supportsDetailedResources) {
      return { to: `${link}/changes`, ariaLabel: "Go to run changes" };
    }

    if (run.delta.addCount) {
      return { to: `${link}/changes/add`, ariaLabel: "Go to run changes" };
    }
    if (run.delta.changeCount) {
      return { to: `${link}/changes/change`, ariaLabel: "Go to run changes" };
    }
    if (run.delta.deleteCount) {
      return { to: `${link}/changes/delete`, ariaLabel: "Go to run changes" };
    }

    // Non-detailed runs with only import/move/forget counts have no changes route
    return { to: link, ariaLabel: "Go to this run" };
  };

  const deltaLink = getDeltaLink();

  const vendorDetails =
    (vendorConfig && run.runtimeConfig && getRuntimeConfigVendorInfo(run.runtimeConfig)) ||
    undefined;

  const vendorIcons = vendorDetails && (
    <>
      <VendorVersion icon={vendorDetails.vendor.icon} text={vendorDetails.vendor.text} />
      {vendorDetails.vendorTool && (
        <VendorVersion icon={vendorDetails.vendorTool.icon} text={vendorDetails.vendorTool.text} />
      )}
    </>
  );

  const handleOnChange = useCallback(
    () => onCheck(run, !checked),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onCheck, checked]
  );
  const [addRunReview, { loading: approveRunLoading }] = useReviewRun({
    refetchQueries: ["ListRuns"],
  });

  const onApproveRun = () =>
    addRunReview(
      { stack: stackId, run: run.id, decision: RunReviewDecision.Approve, note: "" },
      (data) => {
        if (data?.runReview.id) {
          reportSuccess({ message: "Run has been approved" });
        }
      }
    );
  const runNeedsApproval = isRunReviewable(run);

  return (
    <ListEntitiesItem
      ref={rowRef}
      direction="row"
      align="center"
      grid
      gridTemplate={selectable ? COLUMN_ORDER_APPROVAL : COLUMN_ORDER_USER}
      gap={COLUMN_GAP}
      className={cx(styles.item, {
        [styles.needsApproval]: runNeedsApproval,
      })}
      role="row"
    >
      {selectable && (
        <Box role="cell">
          <Toggle
            variant="checkbox"
            id={run.id}
            onChange={handleOnChange}
            isSelected={checked}
            aria-label={checked ? "Unselect this run" : "Select this run"}
          />
        </Box>
      )}
      <Box role="cell" direction="column">
        <Link className={styles.focusOutline} to={link}>
          <Box>
            <RunStateBadge state={run.state} />
          </Box>
        </Link>
      </Box>

      <Box role="rowheader" direction="column">
        <Link className={styles.focusOutline} to={link}>
          <RunMetaInfoListItem run={run} stackId={stackId} />
        </Link>
      </Box>

      <Box role="cell" direction="column">
        <Link
          to={link}
          className={cx(styles.link, styles.focusOutline)}
          aria-label={`Go to run. State ${prepareName(run.state)}. Vendor ${getVendorName(vendorConfig?.__typename)}.${vendorDetails?.vendor.text && ` Version ${vendorDetails?.vendor.text}.`}`}
        >
          {vendorIcons}
        </Link>
      </Box>

      <Box align="center" role="cell">
        {run.delta && (
          <DeltaCounts
            compact
            link={deltaLink.to}
            linkAriaLabel={deltaLink.ariaLabel}
            className={styles.delta}
            delta={run.delta}
            isAnsible={isAnsible}
          />
        )}
      </Box>
      <Box align="center" role="cell">
        <DriftDetectedCell drifted={run.driftDetection} />
      </Box>
      <Box role="cell" justify="end">
        {runNeedsApproval && (hasAtLeastWriteAccess.result || hasAtLeastWriteAccess.message) && (
          <Tooltip
            active={!!hasAtLeastWriteAccess.message}
            on={(props) => (
              <Button
                {...props}
                onPress={onApproveRun}
                variant="secondary"
                size="small"
                disabled={approveRunLoading || !hasAtLeastWriteAccess.result}
                loading={approveRunLoading}
              >
                Approve
              </Button>
            )}
          >
            {hasAtLeastWriteAccess.message}
          </Tooltip>
        )}
      </Box>
    </ListEntitiesItem>
  );
};

export default RunElement;
